import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface PublicEventPage {
  id: string;
  event_id: string;
  slug: string;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  public_title?: string;
  public_description?: string;
  public_location?: string;
  public_start_at?: string;
  public_end_at?: string;
  hero_image_url?: string;
  rsvp_enabled?: boolean;
}

export interface PublicEventRSVP {
  id: string;
  public_page_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  status: 'attending' | 'not_attending' | 'maybe';
  guest_count: number;
  message?: string;
  created_at: string;
  updated_at: string;
}

export const usePublicEventPages = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const createPublicPage = async (eventId: string): Promise<PublicEventPage | null> => {
    setLoading(true);
    try {
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser.user) {
        throw new Error('Utilisateur non authentifié');
      }

      // Get current profile id (required by FK and RLS)
      const { data: profileId, error: profileErr } = await supabase.rpc('current_profile_id');
      if (profileErr) throw profileErr;
      if (!profileId) throw new Error('Profil utilisateur introuvable');

      // Generate unique slug
      const { data: slugData, error: slugError } = await supabase.rpc('generate_public_event_slug');
      if (slugError) throw slugError;

      const { data, error } = await supabase
        .from('public_event_pages')
        .insert({
          event_id: eventId,
          slug: slugData as string,
          created_by: profileId as string
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Page publique créée",
        description: "La page partageable a été créée avec succès"
      });

      return data;
    } catch (error: any) {
      console.error('Error creating public page:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de créer la page publique",
        variant: "destructive"
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getPublicPage = async (slug: string): Promise<any | null> => {
    try {
      const { data, error } = await supabase
        .from('public_event_pages')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error fetching public page:', error);
      return null;
    }
  };

  const submitRSVP = async (
    publicPageId: string, 
    rsvpData: Omit<PublicEventRSVP, 'id' | 'public_page_id' | 'created_at' | 'updated_at'>
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('public_event_rsvp')
        .insert({
          public_page_id: publicPageId,
          ...rsvpData
        });

      if (error) throw error;

      toast({
        title: "RSVP envoyé",
        description: "Votre réponse a été enregistrée avec succès"
      });

      return true;
    } catch (error: any) {
      console.error('Error submitting RSVP:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'envoyer votre réponse",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getEventRSVPs = async (eventId: string): Promise<PublicEventRSVP[]> => {
    try {
      const { data, error } = await supabase
        .from('public_event_rsvp')
        .select(`
          *,
          public_event_pages!inner (
            event_id
          )
        `)
        .eq('public_event_pages.event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(item => ({
        id: item.id,
        public_page_id: item.public_page_id,
        first_name: item.first_name,
        last_name: item.last_name,
        email: item.email,
        phone: item.phone,
        status: item.status as 'attending' | 'not_attending' | 'maybe',
        guest_count: item.guest_count,
        message: item.message,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));
    } catch (error: any) {
      console.error('Error fetching RSVPs:', error);
      return [];
    }
  };

  const getPublicPageForEvent = async (eventId: string): Promise<PublicEventPage | null> => {
    try {
      const { data, error } = await supabase
        .from('public_event_pages')
        .select('*')
        .eq('event_id', eventId)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data || null;
    } catch (error: any) {
      console.error('Error fetching public page for event:', error);
      return null;
    }
  };

  const deactivatePublicPage = async (eventId: string): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('public_event_pages')
        .update({ is_active: false })
        .eq('event_id', eventId);

      if (error) throw error;

      toast({
        title: "Page désactivée",
        description: "La page publique a été désactivée"
      });

      return true;
    } catch (error: any) {
      console.error('Error deactivating public page:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de désactiver la page",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updatePublicPage = async (
    slug: string,
    updates: {
      public_title?: string;
      public_description?: string;
      public_location?: string;
      public_start_at?: string;
      public_end_at?: string;
      hero_image_url?: string;
      rsvp_enabled?: boolean;
    }
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('public_event_pages')
        .update(updates)
        .eq('slug', slug);

      if (error) throw error;

      toast({
        title: "Page mise à jour",
        description: "La page publique a été mise à jour avec succès"
      });

      return true;
    } catch (error: any) {
      console.error('Error updating public page:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour la page",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updatePublicPageByEventId = async (
    eventId: string,
    updates: {
      public_title?: string;
      public_description?: string;
      public_location?: string;
      public_start_at?: string;
      public_end_at?: string;
      hero_image_url?: string;
      rsvp_enabled?: boolean;
    }
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('public_event_pages')
        .update(updates)
        .eq('event_id', eventId)
        .eq('is_active', true);

      if (error) throw error;

      return true;
    } catch (error: any) {
      console.error('Error updating public page by event ID:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const createOrUpdatePublicPage = async (
    eventId: string,
    updates: {
      public_title?: string;
      public_description?: string;
      public_location?: string;
      public_start_at?: string;
      public_end_at?: string;
      hero_image_url?: string;
      rsvp_enabled?: boolean;
    }
  ): Promise<PublicEventPage | null> => {
    try {
      // Check if page exists
      const existingPage = await getPublicPageForEvent(eventId);
      
      if (existingPage) {
        // Update existing page
        const success = await updatePublicPageByEventId(eventId, updates);
        return success ? existingPage : null;
      } else {
        // Create new page with updates
        const newPage = await createPublicPage(eventId);
        if (newPage && Object.keys(updates).length > 0) {
          await updatePublicPageByEventId(eventId, updates);
        }
        return newPage;
      }
    } catch (error: any) {
      console.error('Error in createOrUpdatePublicPage:', error);
      return null;
    }
  };

  return {
    loading,
    createPublicPage,
    getPublicPage,
    submitRSVP,
    getEventRSVPs,
    getPublicPageForEvent,
    deactivatePublicPage,
    updatePublicPage,
    updatePublicPageByEventId,
    createOrUpdatePublicPage
  };
};